import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const RiskMatrix = ({ height, data, setMatrixCardKey }) => {
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [hoveredLine, setHoveredLine] = useState(null);
    ////////console.log("risk matrix data: ", data);
    useEffect(() => {
        if (containerRef.current) {
            setContainerSize({
                width: containerRef.current.offsetWidth,
                height: containerRef.current.offsetHeight,
            });
        }
    }, []);

   

    const points1 = data && data.operational ? data.operational : [];
    const points2 = data && data.financial ? data.financial : [];
    const points3 = data && data.reputational ? data.reputational : [];

    //////////console.log(data);


    const getColor = (x, y) => {
        // Custom color pattern based on the screenshot
        const colors = [
            ["bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#F07167]", "bg-[#F07167]", "bg-[#F07167]"],
            ["bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]", "bg-[#F07167]", "bg-[#F07167]"],
            ["bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]", "bg-[#F07167]"],
            ["bg-[#05769D]", "bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]", "bg-[#FED4AE]"],
            ["bg-[#05769D]", "bg-[#05769D]", "bg-[#09A6B0]", "bg-[#09A6B0]", "bg-[#FED4AE]"]
        ];
        return colors[y][x];
    };

   
   
    const calculateLineCoordinates = (points) => {
        return points.map(({ x, y }) => {
            const cell = document.querySelector(`#cell-${4 - y}-${x}`);
            if (cell) {
                const rect = cell.getBoundingClientRect();
                const containerRect = containerRef.current.getBoundingClientRect();
                const xOffset = rect.left + rect.width / 2 - containerRect.left;
                const yOffset = rect.top + rect.height / 2 - containerRect.top;
                return { x: xOffset, y: yOffset };
            }
            return { x: 0, y: 0 };
        });
    };

    function calculateLineSegments(points, radius, breakDistance) {
        const segments = [];
        const epsilon = 0.01; // Tolerance for floating-point comparison

        for (let i = 0; i < points?.length - 1; i++) {
            const start = points[i];
            const end = points[i + 1];

            // Skip if both x and y coordinates are the same
            if (Math.abs(start.x - end.x) < epsilon && Math.abs(start.y - end.y) < epsilon) {
                //////////console.log(`Skipping identical points: Start(${start.x}, ${start.y}) End(${end.x}, ${end.y})`);
                continue;
            }

            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist > 2 * radius + 2 * breakDistance) { // Ensure the line is long enough to break
                const unitX = dx / dist;
                const unitY = dy / dist;

                // Calculate the new start and end points
                const newStart = {
                    x: start.x + unitX * (radius + breakDistance),
                    y: start.y + unitY * (radius + breakDistance),
                };
                const newEnd = {
                    x: end.x - unitX * (radius + breakDistance),
                    y: end.y - unitY * (radius + breakDistance),
                };

                segments.push([newStart, newEnd]);
                ////////console.log(`Adding segment: Start(${newStart.x}, ${newStart.y}) End(${newEnd.x}, ${newEnd.y})`);
            }
        }
        return segments;
    }

    

    const lineCoordinates1 = calculateLineCoordinates(points1);
    const lineCoordinates2 = calculateLineCoordinates(points2);
    const lineCoordinates3 = calculateLineCoordinates(points3);

    const lineSegments1 = calculateLineSegments(lineCoordinates1, 15, 10);
    const lineSegments2 = calculateLineSegments(lineCoordinates2, 15, 10);
    const lineSegments3 = calculateLineSegments(lineCoordinates3, 15, 10);

    const handleMouseEnter = (lineIndex) => {
        ////////console.log(`Hovered over line: ${lineIndex}`); // Log the line index

        if (lineIndex === 1) {
            setMatrixCardKey?.("operational");
        } else if (lineIndex === 2) {
            setMatrixCardKey?.("financial");
        } else if (lineIndex === 3) {
            setMatrixCardKey?.("reputational");
        }
        setHoveredLine(lineIndex);
        animateLine(lineIndex);
        animateCircles(lineIndex);
    };

    const handleMouseLeave = () => {
        setHoveredLine(null);
    };

    function animateLine(lineIndex) {
        d3.selectAll(`.line${lineIndex}`)
            .each(function(_, index) {
                const path = d3.select(this);
                const totalLength = this.getTotalLength();
                const dashing = "6, 6";
                const dashLength = dashing.split(/[\s,]/)
                    .map(a => parseFloat(a) || 0)
                    .reduce((a, b) => a + b);
                const dashCount = Math.ceil(totalLength / dashLength);
                const newDashes = new Array(dashCount).join(dashing + " ");
                const dashArray = newDashes + " 0, " + totalLength;

                path
                    .attr("stroke-dashoffset", totalLength)
                    .attr("stroke-dasharray", dashArray)
                    .transition()
                    .delay(index * 300) // Delay each segment by 500ms times its index
                    .duration(300)
                    .ease(d3.easeLinear)
                    .attr("stroke-dashoffset", 0);
            });
    };

    function animateCircles(lineIndex) {
        const circles = d3.selectAll(`.circle${lineIndex}`);
        circles.each(function(_, index) {
            d3.select(this)
                .transition()
                .delay(index * 300) // Delay each circle by 300ms times its index
                .duration(300)
                .style("opacity", 1);
        });
    };

    return (
        <div ref={containerRef} className="w-full flex items-center justify-center overflow-hidden relative" style={{ height }}>
            <div className="grid grid-rows-5 grid-cols-5 gap-1 w-full h-full p-4" style={{ position: 'relative' }}>
                {/* SVG for Lines */}
                
                {/* Rows with Y-Axis Labels and Grid Blocks */}
                {["Almost Certain", "Likely", "Possible", "Unlikely", "Rare"].map((likelihood, rowIndex) => (
                    <React.Fragment key={rowIndex}>
                        {/* Grid Blocks */}
                        {[...Array(5)].map((_, colIndex) => (
    <div
        key={`${rowIndex}-${colIndex}`}
        id={`cell-${rowIndex}-${colIndex}`}
        className={`relative flex items-center justify-center border rounded-md ${getColor(colIndex, rowIndex)}`}
        style={{
            height: `${containerSize.height / 5 - 10}px`,
            zIndex: 0,
        }}
    >
        {/* Render points from all sets */}
        {[...points1, ...points2, ...points3].map((point, index) => {
            const isHovered = (hoveredLine === 1 && points1.includes(point)) ||
                              (hoveredLine === 2 && points2.includes(point)) ||
                              (hoveredLine === 3 && points3.includes(point));
            const lineIndex = points1.includes(point) ? 1 : points2.includes(point) ? 2 : 3;
            return (
                point.x === colIndex && point.y === (4 - rowIndex) && (
                    <div
                        key={index}
                        className={`absolute flex items-center justify-center w-10 h-10 rounded-full bg-white border border-black border-2 text-black circle${lineIndex}`}
                        style={{ opacity: isHovered || (hoveredLine === null && lineIndex === 1) ? 1 : 0.2, zIndex: 2 }}
                        onMouseEnter={() => handleMouseEnter(lineIndex)}
                        onMouseLeave={handleMouseLeave}
                    >
                        {point.value}
                    </div>
                )
            );
        })}
    </div>
))}

                    </React.Fragment>
                ))}


<svg
    className="absolute top-0 left-0 w-full h-full"
    xmlns="http://www.w3.org/2000/svg"
    style={{ pointerEvents: 'none', zIndex: 1 }} // Ensure SVG is above grid blocks
>
    {/* Render lines */}
    <g>
        {lineSegments1.map(([start, end], index) => {
            ////////console.log(`Rendering line1: Start(${start.x}, ${start.y}) End(${end.x}, ${end.y})`);
            return (
                <line
                    key={`line1-${index}`}
                    className="line1"
                    x1={start.x}
                    y1={start.y}
                    x2={end.x}
                    y2={end.y}
                    stroke="black"
                    strokeWidth="2"
                    opacity={hoveredLine === null || hoveredLine === 1 ? 1 : 0.2} // Default opacity 1
                    stroke-dasharray="6,6"
                />
            );
        })}
    </g>
    <g>
        {lineSegments2.map(([start, end], index) => {
            ////////console.log(`Rendering line2: Start(${start.x}, ${start.y}) End(${end.x}, ${end.y})`);
            return (
                <line
                    key={`line2-${index}`}
                    className="line2"
                    x1={start.x}
                    y1={start.y}
                    x2={end.x}
                    y2={end.y}
                    stroke="black"
                    strokeWidth="2"
                    opacity={hoveredLine === 2 ? 1 : 0.2}
                    stroke-dasharray="6,6"
                />
            );
        })}
    </g>
    <g>
        {lineSegments3.map(([start, end], index) => {
            ////////console.log(`Rendering line3: Start(${start.x}, ${start.y}) End(${end.x}, ${end.y})`);
            return (
                <line
                    key={`line3-${index}`}
                    className="line3"
                    x1={start.x}
                    y1={start.y}
                    x2={end.x}
                    y2={end.y}
                    stroke="black"
                    strokeWidth="2"
                    opacity={hoveredLine === 3 ? 1 : 0.2}
                    stroke-dasharray="6,6"
                />
            );
        })}
    </g>
</svg>
                
            </div>
        </div>
    );
};

export default RiskMatrix;
